/**
 * billing — the pure core of platform billing, ONE copy for the gym's
 * subscription page, the platform gym editor / billing management / revenue
 * chart, and the quarterly billing e-mail + CSV.
 *
 * All platform prices (tier, MAU band, setup fee) are NET amounts. Whether
 * Hungarian VAT is added on top depends on the customer gym's country and
 * EU VAT ID, following the standard B2B service rules from the seller's
 * (Hungarian) perspective:
 *
 *   - Hungarian customer            → domestic: VAT applies (currency-independent —
 *                                     a HUF/EUR/USD invoice to a Hungarian company
 *                                     all carry Hungarian VAT)
 *   - EU (non-HU) + EU VAT ID       → reverse charge: 0% VAT, the customer
 *                                     self-accounts in their member state
 *   - EU (non-HU), NO EU VAT ID     → unresolved B2B status: VAT is charged
 *                                     (safe default) and the row is flagged
 *                                     incomplete until the VAT ID arrives or a
 *                                     platform admin overrides the status
 *   - outside the EU                → out of scope of Hungarian VAT: 0%
 *
 * The platform admin can override the derived status per gym via
 * `gyms/{id}.billing.vatStatusOverride` (gated on `platform.gym.editBilling`,
 * same as the discount editor — see firestore.rules).
 *
 * Before the shared package the whole VAT computation, the country lists,
 * the money formatter and the quarterly price existed twice (client and the
 * e-mail package) under "MIRROR" comments, and the mirror had drifted once:
 * the server's `guessCountryCode` accepted ANY two letters as a country
 * ("xx" → "XX") and classed such a gym as outside the EU — 0% VAT — where the
 * client rejected the text and fell back to the domestic default.
 */

import { findMauBand } from "./mauBands.js";

// ── Country codes ─────────────────────────────────────────────────────

/** EU member states, ISO 3166-1 alpha-2 (27 members as of 2026). */
export const EU_COUNTRY_CODES = [
  "AT", "BE", "BG", "CY", "CZ", "DE", "DK", "EE", "ES", "FI", "FR", "GR",
  "HR", "HU", "IE", "IT", "LT", "LU", "LV", "MT", "NL", "PL", "PT", "RO",
  "SE", "SI", "SK",
];

/**
 * ISO 3166-1 alpha-2 codes for the billing country selector. Full official
 * list so any gym can pick its country; names come from Intl.DisplayNames
 * at render time (no hardcoded name table to maintain).
 */
export const ALL_COUNTRY_CODES = [
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR", "AS", "AT",
  "AU", "AW", "AX", "AZ", "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI",
  "BJ", "BL", "BM", "BN", "BO", "BQ", "BR", "BS", "BT", "BV", "BW", "BY",
  "BZ", "CA", "CC", "CD", "CF", "CG", "CH", "CI", "CK", "CL", "CM", "CN",
  "CO", "CR", "CU", "CV", "CW", "CX", "CY", "CZ", "DE", "DJ", "DK", "DM",
  "DO", "DZ", "EC", "EE", "EG", "EH", "ER", "ES", "ET", "FI", "FJ", "FK",
  "FM", "FO", "FR", "GA", "GB", "GD", "GE", "GF", "GG", "GH", "GI", "GL",
  "GM", "GN", "GP", "GQ", "GR", "GS", "GT", "GU", "GW", "GY", "HK", "HM",
  "HN", "HR", "HT", "HU", "ID", "IE", "IL", "IM", "IN", "IO", "IQ", "IR",
  "IS", "IT", "JE", "JM", "JO", "JP", "KE", "KG", "KH", "KI", "KM", "KN",
  "KP", "KR", "KW", "KY", "KZ", "LA", "LB", "LC", "LI", "LK", "LR", "LS",
  "LT", "LU", "LV", "LY", "MA", "MC", "MD", "ME", "MF", "MG", "MH", "MK",
  "ML", "MM", "MN", "MO", "MP", "MQ", "MR", "MS", "MT", "MU", "MV", "MW",
  "MX", "MY", "MZ", "NA", "NC", "NE", "NF", "NG", "NI", "NL", "NO", "NP",
  "NR", "NU", "NZ", "OM", "PA", "PE", "PF", "PG", "PH", "PK", "PL", "PM",
  "PN", "PR", "PS", "PT", "PW", "PY", "QA", "RE", "RO", "RS", "RU", "RW",
  "SA", "SB", "SC", "SD", "SE", "SG", "SH", "SI", "SJ", "SK", "SL", "SM",
  "SN", "SO", "SR", "SS", "ST", "SV", "SX", "SY", "SZ", "TC", "TD", "TF",
  "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO", "TR", "TT", "TV", "TW",
  "TZ", "UA", "UG", "UM", "US", "UY", "UZ", "VA", "VC", "VE", "VG", "VI",
  "VN", "VU", "WF", "WS", "YE", "YT", "ZA", "ZM", "ZW",
];

/**
 * Legacy free-text `country` → ISO code, common spellings only (the field
 * used to default to "Magyarország" and accept anything).
 */
const LEGACY_COUNTRY_NAMES = {
  "magyarország": "HU", "magyarorszag": "HU", "hungary": "HU", "ungarn": "HU",
  "ausztria": "AT", "austria": "AT", "österreich": "AT",
  "szlovákia": "SK", "szlovakia": "SK", "slovakia": "SK", "slovensko": "SK",
  "románia": "RO", "romania": "RO", "românia": "RO",
  "németország": "DE", "nemetorszag": "DE", "germany": "DE", "deutschland": "DE",
  "horvátország": "HR", "horvatorszag": "HR", "croatia": "HR",
  "szlovénia": "SI", "szlovenia": "SI", "slovenia": "SI",
  "csehország": "CZ", "csehorszag": "CZ", "czechia": "CZ", "czech republic": "CZ",
  "lengyelország": "PL", "lengyelorszag": "PL", "poland": "PL",
  "szerbia": "RS", "serbia": "RS",
  "ukrajna": "UA", "ukraine": "UA",
  "egyesült királyság": "GB", "united kingdom": "GB", "uk": "GB",
  "svájc": "CH", "svajc": "CH", "switzerland": "CH",
  "egyesült államok": "US", "united states": "US", "usa": "US",
};

/**
 * Best-effort mapping of the legacy free-text `country` field to an ISO
 * code. Only a REAL two-letter code passes ("xx" is not a country); only
 * common spellings are recognised; unknown text returns null and the caller
 * falls back to the domestic default.
 */
export function guessCountryCode(freeText) {
  if (typeof freeText !== "string") return null;
  const s = freeText.trim().toLowerCase();
  if (!s) return null;
  if (/^[a-z]{2}$/.test(s) && ALL_COUNTRY_CODES.includes(s.toUpperCase())) return s.toUpperCase();
  return LEGACY_COUNTRY_NAMES[s] || null;
}

// ── VAT (áfa) ─────────────────────────────────────────────────────────

/**
 * The three values a platform admin can force; "eu_missing_vat_id" is
 * derive-only (it IS the unresolved state an override resolves).
 */
export const VAT_OVERRIDE_VALUES = ["domestic", "reverse_charge", "out_of_scope"];

export const DEFAULT_VAT_RATE = 27;

/**
 * Derive the VAT status from a structured country code + EU VAT ID.
 * Missing/unknown country falls back to "domestic" — the safe default for
 * the seller (VAT gets charged); the operator can override per gym.
 */
export function deriveVatStatus(countryCode, vatId) {
  const cc = typeof countryCode === "string" ? countryCode.trim().toUpperCase() : "";
  if (!cc || cc === "HU") return "domestic";
  if (EU_COUNTRY_CODES.includes(cc)) {
    return (typeof vatId === "string" && vatId.trim()) ? "reverse_charge" : "eu_missing_vat_id";
  }
  return "out_of_scope";
}

/**
 * The fields of `gyms/{gymId}/private/invoiceDetails` — the gym's invoice
 * identity. It moved off the publicly readable gym doc (it is PII: an
 * individual entrepreneur's company and contact names are a person's name);
 * the security rules hold the private doc to exactly these keys.
 */
export const INVOICE_DETAILS_FIELDS = [
  "companyName", "taxNumber", "vatId", "countryCode", "country",
  "postalCode", "city", "address", "contactName", "invoiceEmail", "updatedAt",
];

/** The values `billing.derivedVatStatus` can hold (besides null). */
export const DERIVED_VAT_STATUSES = ["domestic", "reverse_charge", "eu_missing_vat_id", "out_of_scope"];

/**
 * The PII-free projection of the private invoice details that the public gym
 * doc carries in its `billing` map, kept by the `syncGymInvoiceDetailsStatus`
 * trigger: whether the company name AND the tax number are present, and the
 * VAT status derived from the address WITHOUT the platform override (the
 * override sits on the public doc already, so readers add it themselves —
 * which is why one trigger on the private doc is enough). Legacy details that
 * only have the free-text `country` are mapped through guessCountryCode.
 */
export function summarizeInvoiceDetails(invoiceDetails) {
  const d = invoiceDetails && typeof invoiceDetails === "object" ? invoiceDetails : null;
  if (!d) return { detailsCore: false, derivedVatStatus: null };
  const cc = d.countryCode || guessCountryCode(d.country);
  return {
    detailsCore: !!(d.companyName && d.taxNumber),
    derivedVatStatus: deriveVatStatus(cc, d.vatId),
  };
}

/**
 * Effective VAT status for a gym's `billing` map: the platform admin
 * override wins; otherwise the projected `derivedVatStatus`; with neither
 * (no invoice details yet) the safe "domestic" default — VAT gets charged.
 * A caller holding unsaved details merges `summarizeInvoiceDetails(form)` in.
 */
export function gymVatStatus(billing) {
  const override = billing?.vatStatusOverride;
  if (VAT_OVERRIDE_VALUES.includes(override)) return override;
  const derived = billing?.derivedVatStatus;
  return DERIVED_VAT_STATUSES.includes(derived) ? derived : "domestic";
}

/** Does the status put VAT on the invoice? */
export function vatApplies(status) {
  return status === "domestic" || status === "eu_missing_vat_id";
}

/** HUF is invoiced in whole forints; EUR/USD to 2 decimals. */
export function roundMoney(amount, currency) {
  if (currency === "HUF") return Math.round(amount);
  return Math.round(amount * 100) / 100;
}

/**
 * VAT breakdown on top of a NET total. `vatRate` is the configured platform
 * rate (pricing.vatRate); the returned `vatRate` is the EFFECTIVE rate
 * (0 for reverse charge / out of scope).
 * Returns { vatApplies, vatRate, vatAmount, gross }.
 */
export function computeVat(netTotal, vatRate, currency, status) {
  const applies = vatApplies(status);
  const rate = applies && typeof vatRate === "number" && vatRate > 0 ? vatRate : 0;
  const vatAmount = rate > 0 ? roundMoney((netTotal * rate) / 100, currency) : 0;
  return {
    vatApplies: rate > 0,
    vatRate: rate,
    vatAmount,
    gross: roundMoney(netTotal + vatAmount, currency),
  };
}

// ── Money for the eye ─────────────────────────────────────────────────

/**
 * One formatter for a price with its currency.
 *
 * Three app surfaces (the platform revenue chart, the gym's subscription
 * page, the platform gym editor) each had a `formatPrice` and disagreed: one
 * wrote `$1,234`, another `1,234 USD`; two used the OS locale
 * (`toLocaleString()` with no argument), so the same forint figure grouped
 * differently on a Hungarian and an English machine. This is the app's rule,
 * and it is the rule the quarterly billing e-mail applies.
 *
 * Deliberately NOT the reader's locale: a currency amount is grouped the way
 * that currency is written, not the way the reader's language writes dates.
 * HUF is whole forints; EUR/USD may carry cents (VAT on 39 EUR is 10.53),
 * shown only when present.
 *
 * Not to be confused with `formatCurrency` (`apps/web/src/competition/settings.js`),
 * which formats a bare entry-fee NUMBER without its currency for the
 * competition surfaces.
 */
export function formatMoney(amount, currency) {
  const cur = currency || "HUF";
  const n = Number(amount || 0);
  if (cur === "HUF") return `${n.toLocaleString("hu-HU")} Ft`;
  const opts = { minimumFractionDigits: 0, maximumFractionDigits: 2 };
  if (cur === "EUR") return `${n.toLocaleString("en-IE", opts)} EUR`;
  return `${n.toLocaleString("en-US", opts)} USD`;
}

// ── Quarterly pricing ─────────────────────────────────────────────────

/** A price field as a number; anything unusable (missing, NaN, a string) is 0, never NaN. */
function moneyOrZero(v) {
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

/**
 * A discount percentage as a usable number in [0, 100]: above 100 is capped,
 * anything that is not a positive finite number (missing, NaN, a string,
 * negative, 0) is no discount. ONE rule for both discount levels — the
 * gym-wide `billing.discountPercent` and the per-add-on
 * `billing.addonDiscounts.<key>` — so a hand-edited doc can never produce a
 * negative or NaN price at either level.
 */
export function clampDiscountPercent(v) {
  return (typeof v === "number" && Number.isFinite(v) && v > 0) ? Math.min(v, 100) : 0;
}

/**
 * The quarterly NET total of a gym: plan tier + MAU band + every add-on
 * charge, minus the discounts. Two levels, applied in this order: first each
 * add-on's own discount (`addonDiscounts`), then the gym-wide discount on the
 * WHOLE sum (tier + MAU + the already line-discounted add-ons). Sequential,
 * not additive: 50% on an add-on and 10% gym-wide takes 55% off that add-on.
 *
 * The caller looks the prices up (`platform/pricing` tiers and bands, and
 * `addonQuarterCharges` for the add-ons), so this is the one place the sum
 * and the discounts are computed — the gym's subscription page, the platform
 * gym editor and the quarterly invoice all run it. It replaced
 * `calculateQuarterlyPrice`, which looked the two prices up itself, knew no
 * add-ons and discounted only tier + MAU; with no add-on charge the result is
 * the same number.
 *
 * Without `addonDiscounts` every field except the two new add-on ones is
 * exactly what it was before the per-add-on discount existed. A 100% line
 * discount keeps the row in `addonLines` at price 0, so the invoice still
 * shows that the add-on was switched on.
 *
 * @param {object} p
 * @param {number} p.tierPrice - NET plan price for the quarter
 * @param {number} p.mauPrice - NET MAU band price for the quarter
 * @param {Array<{ key: string, price: number }>} [p.addonCharges] - `addonQuarterCharges` output
 * @param {Record<string, number>} [p.addonDiscounts] - `billing.addonDiscounts`: add-on key → 0–100; a non-object, unknown keys and bad values are ignored
 * @param {number} [p.discountPercent=0] - gym-wide, 0–100; above 100 is capped, non-numbers and ≤ 0 are no discount
 * @param {string} [p.currency="HUF"] - rounding: HUF whole forints, EUR/USD cents
 * @returns {{
 *   tierPrice: number, mauPrice: number,
 *   addonTotal: number, addonDiscount: number,
 *   addonLines: Array<{ key: string, listPrice: number, discountPercent: number, discount: number, price: number }>,
 *   discount: number, total: number,
 * }} `addonTotal` is AFTER the line discounts (it is what enters the subtotal);
 *   `addonDiscount` is their sum; `discount` is the gym-wide one only.
 */
export function quarterlyTotal({ tierPrice, mauPrice, addonCharges, addonDiscounts, discountPercent, currency } = {}) {
  const cur = currency || "HUF";
  const tier = moneyOrZero(tierPrice);
  const mau = moneyOrZero(mauPrice);
  // An array or a scalar here is a corrupt doc, not a discount map.
  const lineDiscounts = addonDiscounts && typeof addonDiscounts === "object" && !Array.isArray(addonDiscounts)
    ? addonDiscounts
    : null;
  const addonLines = (Array.isArray(addonCharges) ? addonCharges : []).map((c) => {
    const listPrice = moneyOrZero(c?.price);
    const pct = lineDiscounts && typeof c?.key === "string" ? clampDiscountPercent(lineDiscounts[c.key]) : 0;
    // Same currency-aware rounding as the gym-wide discount below.
    const lineDiscount = pct > 0 ? roundMoney((listPrice * pct) / 100, cur) : 0;
    // An undiscounted line keeps its raw price: rounding it per line would
    // change the old "round the SUM" result (HUF 0.4 + 0.4 → 1, not 0).
    const price = pct > 0 ? roundMoney(listPrice - lineDiscount, cur) : listPrice;
    return { key: c?.key, listPrice, discountPercent: pct, discount: lineDiscount, price };
  });
  const addonTotal = roundMoney(addonLines.reduce((sum, l) => sum + l.price, 0), cur);
  const addonDiscount = roundMoney(addonLines.reduce((sum, l) => sum + l.discount, 0), cur);
  const subtotal = tier + mau + addonTotal;
  const pct = clampDiscountPercent(discountPercent);
  // Currency-aware rounding: HUF invoices carry whole forints, EUR/USD
  // keep cents (5% of 39 EUR = 1.95, not 2).
  const discount = pct > 0 ? roundMoney((subtotal * pct) / 100, cur) : 0;
  return { tierPrice: tier, mauPrice: mau, addonTotal, addonDiscount, addonLines, discount, total: roundMoney(subtotal - discount, cur) };
}

/**
 * Parse a "YYYY-Qn" quarter label into { label, year, quarter, months:
 * ["YYYY-MM", ×3] } — the shape the e-mail package's `previousQuarter`
 * returns, so a requested past quarter and the default one are
 * interchangeable. Returns null on malformed input (a hand-edited marker doc
 * must not crash the revenue chart; the callable throws on it).
 */
export function parseQuarterLabel(label) {
  if (typeof label !== "string") return null;
  const m = label.match(/^(\d{4})-Q([1-4])$/);
  if (!m) return null;
  const year = Number(m[1]);
  const q = Number(m[2]);
  const startMonth0 = (q - 1) * 3;
  const months = [0, 1, 2].map((i) => `${year}-${String(startMonth0 + i + 1).padStart(2, "0")}`);
  return { label, year, quarter: q, months };
}

// ── Who gets a quarterly invoice ──────────────────────────────────────
//
// ONE rule for every surface that asks "will this gym be invoiced, and can
// the invoice be issued": the quarterly billing e-mail + CSV, the per-gym
// regenerate callable, the platform gym list badge, billing management and
// the MCP billing tools. Before this block each of them spelled its own
// version, and they disagreed: the gym list skipped every Free gym while the
// e-mail invoiced a Free gym over 100 MAU for its band fee.

/**
 * How far the gym's invoice details are, read from the public projection:
 *   "missing"  — no company name or no tax number (or no projection yet);
 *   "core"     — both present, but the VAT status is unresolved (an EU gym
 *                without an EU VAT ID can't get its reverse-charge invoice
 *                issued — the operator chases the VAT ID or overrides it);
 *   "complete" — the invoice can be issued.
 * The onboarding checklist accepts "core" (`!== "missing"`); every invoicing
 * surface needs "complete". Takes the `billing` map, so a caller holding a
 * live VAT override or unsaved details passes them merged in.
 */
export function invoiceDetailsStatus(billing) {
  if (billing?.detailsCore !== true) return "missing";
  return gymVatStatus(billing) === "eu_missing_vat_id" ? "core" : "complete";
}

/** A demo gym is ours, not a customer: it is never invoiced, whatever its plan. */
export function isBillableGym(gym) {
  return !!gym && gym.isDemo !== true;
}

/**
 * Does this gym get a quarterly invoice? A billable gym whose NET quarterly
 * total (`quarterlyTotal(...).total` — tier + MAU band + add-ons (after their
 * own line discounts) − the gym-wide discount)
 * is above zero. So a Free gym over the free MAU band IS invoiced, and a 100%
 * discount is not: 0 Ft invoices are never issued.
 */
export function needsQuarterlyInvoice(gym, total) {
  return isBillableGym(gym) && typeof total === "number" && total > 0;
}

/** The gym will be invoiced, but its invoice details don't allow it yet. */
export function isInvoiceBlocked(gym, total) {
  return needsQuarterlyInvoice(gym, total) && invoiceDetailsStatus(gym?.billing) !== "complete";
}

/**
 * The UTC "YYYY-MM" keys of the quarter containing `now`, up to and
 * including `now`'s own month — the months the NEXT invoice's MAU band is
 * picked from so far. UTC, like the billing run and `parseQuarterLabel`.
 */
export function quarterMonthsToDate(now) {
  const d = now instanceof Date ? now : new Date(now);
  const year = d.getUTCFullYear();
  const month0 = d.getUTCMonth();
  const start0 = Math.floor(month0 / 3) * 3;
  const out = [];
  for (let m = start0; m <= month0; m++) out.push(`${year}-${String(m + 1).padStart(2, "0")}`);
  return out;
}

/** The highest MAU across `months` in a month → count map; missing or bad values count as 0. */
export function maxMauOver(byMonth, months) {
  const values = (Array.isArray(months) ? months : []).map((m) => {
    const n = Number(byMonth?.[m]);
    return Number.isFinite(n) && n > 0 ? n : 0;
  });
  return Math.max(0, ...values);
}

/**
 * The month → MAU map a `gyms/{gymId}/derived/mau` snapshot
 * (`{ history, currentMau, currentMonth }`, written daily by the MAU
 * snapshot routine) gives for `now`: its `history`, with `currentMau` laid
 * over now's month — the fresher figure. A snapshot from an EARLIER month
 * (the daily run has not rolled over yet) does not lend its `currentMau` to
 * this month. A missing snapshot gives an empty map.
 */
export function mauByMonthFromSnapshot(snapshot, now) {
  const history = snapshot?.history;
  const out = history && typeof history === "object" ? { ...history } : {};
  const months = quarterMonthsToDate(now);
  const thisMonth = months[months.length - 1];
  const cm = snapshot?.currentMonth;
  if (typeof snapshot?.currentMau === "number" && (!cm || cm === thisMonth)) out[thisMonth] = snapshot.currentMau;
  return out;
}

/** The quarter-to-date peak MAU from a `derived/mau` snapshot — what the next invoice's band is picked from so far. */
export function quarterPeakMau(snapshot, now) {
  return maxMauOver(mauByMonthFromSnapshot(snapshot, now), quarterMonthsToDate(now));
}

/**
 * One gym's quarterly NET price: the tier price of its plan, the band price
 * for `maxMau`, the add-on charges, and its discount — all in the gym's
 * billing currency. `addonCharges` is `[{ key, price }]` (the
 * `addonQuarterCharges` output, or a live estimate from the current flags).
 * The gym's `billing.addonDiscounts` is passed on, so every caller that
 * prices through here applies the per-add-on discounts without knowing them.
 * Returns `quarterlyTotal`'s breakdown plus the resolved `plan`, `currency`,
 * `band` and `bandId`.
 */
export function gymQuarterPrice({ gym, pricing, maxMau, addonCharges } = {}) {
  const billing = gym?.billing || {};
  const plan = gym?.plan || "free";
  const currency = billing.currency || "HUF";
  const band = findMauBand(pricing?.mauBands, maxMau);
  const bandId = band?.id || "band_0";
  const prices = quarterlyTotal({
    tierPrice: pricing?.tiers?.[plan]?.prices?.[currency] || 0,
    mauPrice: pricing?.mauBands?.find((b) => b.id === bandId)?.prices?.[currency] || 0,
    addonCharges,
    addonDiscounts: billing.addonDiscounts,
    discountPercent: billing.discountPercent || 0,
    currency,
  });
  return { ...prices, plan, currency, band: band || null, bandId };
}

// ── Quarterly summary recipients ───────────────────────────────────────

/** How many addresses the quarterly summary may go to. A hand-kept operator
 *  list, not a mailing list — 20 is generous, and it bounds both the editor's
 *  read and the billing run's. */
export const BILLING_RECIPIENT_MAX = 20;

/** Max length of a recipient's operator-facing label ("accountant"). Never
 *  sent anywhere: it exists so the list still makes sense in six months. */
export const BILLING_RECIPIENT_LABEL_MAX = 60;

// Deliberately loose: one `@`, a dot-suffixed domain, no whitespace and none
// of the separators a pasted list would carry (`,` `;`). The point is to
// catch a typo and a pasted "Name <a@b.hu>", not to re-implement RFC 5322 —
// a wrong-but-well-formed address is a bounce, which Resend reports, while a
// rejected valid address is an invoice summary nobody receives.
const RECIPIENT_EMAIL_RE = /^[^\s@,;<>]+@[^\s@,;<>]+\.[a-z]{2,}$/i;

/**
 * The address as it is stored and sent: trimmed and lowercased, or `null` if
 * it is not an address at all. Lowercase because the document id IS the
 * address — two casings of one mailbox would otherwise be two documents, and
 * the summary would arrive twice.
 */
export function normalizeBillingRecipientEmail(raw) {
  if (typeof raw !== "string") return null;
  const email = raw.trim().toLowerCase();
  if (!email || email.length > 200) return null;
  return RECIPIENT_EMAIL_RE.test(email) ? email : null;
}

/** The label as stored: whitespace collapsed, clipped to the field's limit. */
export function normalizeBillingRecipientLabel(raw) {
  if (typeof raw !== "string") return "";
  return raw.trim().replace(/\s+/g, " ").slice(0, BILLING_RECIPIENT_LABEL_MAX);
}

/**
 * The send list from stored recipient documents: `[{ email, label }]`, invalid
 * addresses dropped, duplicates collapsed on the normalized address, capped at
 * `BILLING_RECIPIENT_MAX`. Accepts `{ email }` objects or bare strings.
 *
 * The billing run filters through this rather than trusting the documents: a
 * single bad row must not cost the whole quarter its summary.
 */
export function normalizeBillingRecipients(entries) {
  const out = [];
  const seen = new Set();
  for (const entry of Array.isArray(entries) ? entries : []) {
    const email = normalizeBillingRecipientEmail(typeof entry === "string" ? entry : entry?.email);
    if (!email || seen.has(email)) continue;
    seen.add(email);
    out.push({ email, label: normalizeBillingRecipientLabel(typeof entry === "string" ? "" : entry?.label) });
    if (out.length >= BILLING_RECIPIENT_MAX) break;
  }
  return out;
}
